// =============================================================================
// Global Project Sync Store - Zustand
// Shared state across all project views for instant synchronization
// Terminal status codes are loaded dynamically from the database
// =============================================================================

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { Task } from '../types';
import type { Milestone, MilestoneProgress, ProjectProgress } from '../data/milestoneRepository';
import * as taskRepo from '../data/taskRepository';
import { fetchTerminalStatusCodes } from '../data/statusRepository';
import { useSidebarProjectsStore } from '../hooks/useSidebarProjectsStore';

/** Adjust sidebar task count when a parent task is added/removed/status-changed */
const adjustSidebarCount = (projectId: string, delta: number) => {
  useSidebarProjectsStore.getState().adjustTaskCount(projectId, delta);
};
import {
  fetchProjectMilestones,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  assignMilestoneToTask,
  fetchMilestoneProgress,
  fetchProjectProgress,
  fetchMilestoneById,
  updateMilestonePositions,
  type MilestoneCreateInput,
  type MilestoneUpdateInput,
} from '../data/milestoneRepository';

// =============================================================================
// Types
// =============================================================================

interface ProjectSyncState {
  // Current project context
  projectId: string | null;
  orgId: string | null;
  
  // Terminal status codes (loaded from DB, used for sidebar counters)
  terminalStatusCodes: string[];
  
  // Tasks
  tasks: Task[];
  tasksLoading: boolean;
  tasksSyncing: boolean;
  tasksError: string | null;
  
  // Milestones
  milestones: Milestone[];
  milestonesLoading: boolean;
  milestonesError: string | null;
  selectedMilestoneId: string | null;
  
  // Progress cache
  milestoneProgress: Record<string, MilestoneProgress>;
  projectProgress: ProjectProgress | null;
  
  // Sync metadata
  lastSyncAt: number | null;
  pendingMilestoneUpdates: Set<string>;
}

interface ProjectSyncActions {
  // Context
  setContext: (projectId: string, orgId: string) => void;
  clearContext: () => void;
  
  // Tasks
  loadTasks: () => Promise<void>;
  addTask: (taskData: Partial<Task>, userId: string) => Promise<Task | undefined>;
  updateTask: (taskId: string, updates: Partial<Task>, userId?: string) => Promise<void>;
  moveTask: (taskId: string, newStatusCode: string, newPosition: number) => Promise<void>;
  deleteTask: (taskId: string, userId?: string) => Promise<void>;
  duplicateTask: (taskId: string, userId: string) => Promise<Task | undefined>;
  reorderTask: (taskId: string, newPosition: number) => Promise<void>;
  
  // Task realtime updates (from other clients)
  realtimeInsertTask: (task: Task) => void;
  realtimeUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  realtimeDeleteTask: (taskId: string) => void;
  
  // Milestones
  loadMilestones: () => Promise<void>;
  addMilestone: (input: MilestoneCreateInput) => Promise<Milestone | null>;
  editMilestone: (id: string, patch: MilestoneUpdateInput) => Promise<boolean>;
  removeMilestone: (id: string) => Promise<boolean>;
  selectMilestone: (id: string | null) => void;
  assignMilestoneToTask: (taskId: string, milestoneId: string | null) => Promise<boolean>;
  reorderMilestones: (milestones: Milestone[]) => Promise<boolean>;
  reloadSingleMilestone: (milestoneId: string) => Promise<Milestone | null>;
  
  // Milestone realtime updates
  realtimeUpdateMilestone: (milestoneId: string, updates: Partial<Milestone>) => void;
  
  // Progress
  loadMilestoneProgress: (milestoneId: string) => Promise<MilestoneProgress | null>;
  loadProjectProgress: () => Promise<ProjectProgress | null>;
  
  // Sync helpers
  refreshAffectedMilestones: (milestoneIds: (string | null | undefined)[]) => Promise<void>;
  markMilestonePending: (milestoneId: string) => void;
}

type ProjectSyncStore = ProjectSyncState & ProjectSyncActions;

// =============================================================================
// Normalize realtime payloads (snake_case DB columns → Task-compatible keys)
// Prevents flickering when raw Postgres payloads overwrite local state
// =============================================================================
function normalizeRealtimeTask(raw: any): Partial<Task> {
  const result: any = {};
  // Map every known Task field; accept both camelCase and snake_case
  const directFields = [
    'id', 'org_id', 'project_id', 'parent_task_id', 'title', 'description',
    'status_code', 'priority', 'due_at', 'milestone_id', 'kanban_position',
    'created_at', 'updated_at', 'created_by', 'updated_by',
    'estimated_hours', 'start_date', 'complexity',
    'recurrence_enabled', 'recurrence_rule', 'next_occurrence_at',
    'owner_member_id',
  ];
  for (const field of directFields) {
    if (field in raw) {
      result[field] = raw[field];
    }
  }
  return result;
}

// =============================================================================
// Store
// =============================================================================

export const useProjectSyncStore = create<ProjectSyncStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    projectId: null,
    orgId: null,
    terminalStatusCodes: ['done'],
    tasks: [],
    tasksLoading: false,
    tasksSyncing: false,
    tasksError: null,
    milestones: [],
    milestonesLoading: false,
    milestonesError: null,
    selectedMilestoneId: null,
    milestoneProgress: {},
    projectProgress: null,
    lastSyncAt: null,
    pendingMilestoneUpdates: new Set(),

    // ─────────────────────────────────────────────────────────────────────────
    // Context
    // ─────────────────────────────────────────────────────────────────────────
    setContext: (projectId, orgId) => {
      const current = get();
      // Only reset if project changed
      if (current.projectId !== projectId) {
        set({
          projectId,
          orgId,
          terminalStatusCodes: ['done'],
          tasks: [],
          milestones: [],
          milestoneProgress: {},
          projectProgress: null,
          lastSyncAt: null,
          pendingMilestoneUpdates: new Set(),
        });
      } else {
        set({ projectId, orgId });
      }
      // Load terminal status codes for this specific project
      fetchTerminalStatusCodes(orgId, projectId).then(codes => {
        set({ terminalStatusCodes: codes.length > 0 ? codes : ['done'] });
      }).catch(() => {
        set({ terminalStatusCodes: ['done'] });
      });
    },

    clearContext: () => {
      set({
        projectId: null,
        orgId: null,
        terminalStatusCodes: ['done'],
        tasks: [],
        milestones: [],
        milestoneProgress: {},
        projectProgress: null,
        lastSyncAt: null,
        pendingMilestoneUpdates: new Set(),
      });
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Load
    // ─────────────────────────────────────────────────────────────────────────
    loadTasks: async () => {
      const { projectId } = get();
      if (!projectId) return;

      set({ tasksLoading: true });
      try {
        const tasks = await taskRepo.fetchTasks(projectId);
        set({ tasks, tasksLoading: false, tasksSyncing: false, tasksError: null });
      } catch (err: any) {
        set({ tasksError: err.message, tasksLoading: false });
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Add
    // ─────────────────────────────────────────────────────────────────────────
    addTask: async (taskData, userId) => {
      const { projectId, orgId, tasks } = get();
      if (!projectId || !orgId) return;

      const parentTasks = tasks.filter(
        (t) => t.status_code === (taskData.status_code || 'todo') && !t.parent_task_id
      );
      const maxPosition = parentTasks.reduce((max, t) => Math.max(max, t.kanban_position), 0);

      const tempId = `temp-${Date.now()}`;
      const newTask: Task = {
        id: tempId,
        org_id: orgId,
        project_id: projectId,
        parent_task_id: null,
        title: taskData.title || '',
        description: taskData.description || null,
        status_code: taskData.status_code || 'todo',
        priority: taskData.priority || null,
        due_at: taskData.due_at || null,
        milestone_id: taskData.milestone_id || null,
        kanban_position: maxPosition + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: userId,
        updated_by: userId,
      };

      // Optimistic update
      set({ tasks: [...tasks, newTask] });

      try {
        const created = await taskRepo.createTask(
          orgId,
          projectId,
          { ...taskData, kanban_position: maxPosition + 1 },
          userId
        );

        // Replace temp with real
        set(state => ({
          tasks: state.tasks.filter(t => t.id !== tempId).concat(created)
        }));

        await taskRepo.createAuditLog(orgId, userId, 'create', created.id, undefined, {
          title: taskData.title,
          project_id: projectId,
        });

        // Refresh affected milestone
        if (created.milestone_id) {
          get().refreshAffectedMilestones([created.milestone_id]);
        }

        // Update sidebar count: new parent task with non-terminal status
        if (!created.parent_task_id && !get().terminalStatusCodes.includes(created.status_code)) {
          adjustSidebarCount(projectId, +1);
        }

        return created;
      } catch (err) {
        console.error('Add task failed, rolling back:', err);
        set(state => ({ tasks: state.tasks.filter(t => t.id !== tempId) }));
        throw err;
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Update
    // ─────────────────────────────────────────────────────────────────────────
    updateTask: async (taskId, updates, userId) => {
      const { tasks, orgId } = get();
      const originalTask = tasks.find(t => t.id === taskId);
      if (!originalTask) return;

      // Optimistic update — set updated_at to NOW so that the realtime echo
      // (which will carry the same or slightly older server timestamp) is
      // ignored by realtimeUpdateTask, preventing the re-render "vibration".
      const optimisticUpdatedAt = new Date().toISOString();
      set({
        tasks: tasks.map(t => t.id === taskId ? { ...t, ...updates, updated_at: optimisticUpdatedAt } : t)
      });

      try {
        await taskRepo.updateTask(taskId, updates, userId);
        
        // Refresh affected milestones (old and new if changed)
        const affectedMilestones = [originalTask.milestone_id];
        if (updates.milestone_id !== undefined && updates.milestone_id !== originalTask.milestone_id) {
          affectedMilestones.push(updates.milestone_id);
        }
        if (updates.status_code !== undefined && updates.status_code !== originalTask.status_code) {
          // Status change affects milestone progress
          get().refreshAffectedMilestones(affectedMilestones);
          
          // Update sidebar count for parent tasks
          if (!originalTask.parent_task_id) {
            const wasDone = get().terminalStatusCodes.includes(originalTask.status_code);
            const isDone = get().terminalStatusCodes.includes(updates.status_code);
            if (!wasDone && isDone) adjustSidebarCount(originalTask.project_id, -1);
            if (wasDone && !isDone) adjustSidebarCount(originalTask.project_id, +1);
          }
        }
      } catch (err) {
        console.error('Update task failed, rolling back:', err);
        set({
          tasks: get().tasks.map(t => t.id === taskId ? originalTask : t)
        });
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Move (Kanban)
    // ─────────────────────────────────────────────────────────────────────────
    moveTask: async (taskId, newStatusCode, newPosition) => {
      const { tasks } = get();
      const originalTask = tasks.find(t => t.id === taskId);
      if (!originalTask) return;

      // Optimistic update with position recalculation
      const targetColumnTasks = tasks
        .filter(t => t.status_code === newStatusCode && t.id !== taskId && !t.parent_task_id)
        .sort((a, b) => a.kanban_position - b.kanban_position);

      const updatedTasks = tasks.map(t => {
        if (t.id === taskId) {
          return { ...t, status_code: newStatusCode, kanban_position: newPosition };
        }
        if (t.status_code === newStatusCode && !t.parent_task_id) {
          const sortedIndex = targetColumnTasks.findIndex(ct => ct.id === t.id);
          if (sortedIndex === -1) return t;
          const newPos = sortedIndex < newPosition ? sortedIndex : sortedIndex + 1;
          return { ...t, kanban_position: newPos };
        }
        return t;
      });

      set({ tasks: updatedTasks, tasksSyncing: true });

      // Update sidebar count if status crosses terminal boundary (parent tasks only)
      if (!originalTask.parent_task_id) {
        const wasDone = get().terminalStatusCodes.includes(originalTask.status_code);
        const isDone = get().terminalStatusCodes.includes(newStatusCode);
        if (!wasDone && isDone) adjustSidebarCount(originalTask.project_id, -1);
        if (wasDone && !isDone) adjustSidebarCount(originalTask.project_id, +1);
      }

      try {
        await taskRepo.moveTaskKanban(taskId, newStatusCode, newPosition);
        set({ tasksSyncing: false });
        
        // Reconcile local state with server after the edge function
        // repositioned all tasks in the column (avoids stale positions & ghost duplicates)
        get().loadTasks();
        
        // Milestone refresh is handled by realtime subscriptions (task change + milestone trigger)
      } catch (err) {
        // Edge function unreachable or rejected — fall back to a direct update.
        // This keeps the UI responsive and lets realtime propagate to other users.
        if (import.meta.env.DEV) {
          console.debug('[projectSyncStore] move-task-kanban edge function unavailable, using direct update fallback', err);
        }
        // Fallback: update status_code and kanban_position directly via Supabase
        try {
          await taskRepo.updateTask(taskId, {
            status_code: newStatusCode,
            kanban_position: newPosition,
          } as any);
          set({ tasksSyncing: false });
          
          // Milestone refresh is handled by realtime subscriptions
        } catch (fallbackErr) {
          console.error('Move task fallback also failed, rolling back:', fallbackErr);
          set({
            tasks: get().tasks.map(t => t.id === taskId ? originalTask : t),
            tasksSyncing: false
          });
        }
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Delete
    // ─────────────────────────────────────────────────────────────────────────
    deleteTask: async (taskId, userId) => {
      const { tasks, orgId } = get();
      const task = tasks.find(t => t.id === taskId);
      if (!task || !orgId) return;

      const subtasks = tasks.filter(t => t.parent_task_id === taskId);
      const milestoneId = task.milestone_id;

      // Optimistic delete
      set({
        tasks: tasks.filter(t => t.id !== taskId && t.parent_task_id !== taskId)
      });

      try {
        for (const subtask of subtasks) {
          await taskRepo.deleteTask(subtask.id);
        }
        await taskRepo.deleteTask(taskId);

        if (userId) {
          await taskRepo.createAuditLog(orgId, userId, 'delete', taskId, {
            title: task.title,
            project_id: task.project_id,
          });
        }

        // Refresh milestone
        if (milestoneId) {
          get().refreshAffectedMilestones([milestoneId]);
        }

        // Update sidebar count: deleted parent task with non-terminal status
        if (!task.parent_task_id && !get().terminalStatusCodes.includes(task.status_code)) {
          adjustSidebarCount(task.project_id, -1);
        }
      } catch (err) {
        console.error('Delete task failed, rolling back:', err);
        set({
          tasks: [...get().tasks, task, ...subtasks]
        });
        throw err;
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Duplicate
    // ─────────────────────────────────────────────────────────────────────────
    duplicateTask: async (taskId, userId) => {
      const { tasks, orgId, projectId } = get();
      const task = tasks.find(t => t.id === taskId);
      if (!task || !orgId || !projectId) return;

      const sameColumnTasks = tasks.filter(
        t => t.status_code === task.status_code && !t.parent_task_id
      );
      const maxPosition = sameColumnTasks.reduce((max, t) => Math.max(max, t.kanban_position), 0);

      const tempId = `temp-${Date.now()}`;
      const duplicatedTask: Task = {
        ...task,
        id: tempId,
        title: `${task.title} (copie)`,
        kanban_position: maxPosition + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: userId,
        updated_by: userId,
      };

      set({ tasks: [...tasks, duplicatedTask] });

      try {
        const created = await taskRepo.createTask(
          orgId,
          projectId,
          {
            title: duplicatedTask.title,
            description: task.description,
            status_code: task.status_code,
            priority: task.priority,
            due_at: task.due_at,
            milestone_id: task.milestone_id,
            kanban_position: maxPosition + 1,
          },
          userId
        );

        set(state => ({
          tasks: state.tasks.filter(t => t.id !== tempId).concat(created)
        }));

        await taskRepo.createAuditLog(orgId, userId, 'duplicate', created.id, undefined, {
          title: duplicatedTask.title,
          original_task_id: taskId,
        });

        if (created.milestone_id) {
          get().refreshAffectedMilestones([created.milestone_id]);
        }

        return created;
      } catch (err) {
        console.error('Duplicate task failed, rolling back:', err);
        set(state => ({ tasks: state.tasks.filter(t => t.id !== tempId) }));
        throw err;
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Reorder
    // ─────────────────────────────────────────────────────────────────────────
    reorderTask: async (taskId, newPosition) => {
      const { tasks } = get();
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const parentTasks = tasks
        .filter(t => !t.parent_task_id)
        .sort((a, b) => a.kanban_position - b.kanban_position);

      const currentIndex = parentTasks.findIndex(t => t.id === taskId);
      if (currentIndex === -1 || currentIndex === newPosition) return;

      const reordered = [...parentTasks];
      const [removed] = reordered.splice(currentIndex, 1);
      reordered.splice(newPosition, 0, removed);

      const positionMap = new Map<string, number>();
      reordered.forEach((t, idx) => positionMap.set(t.id, idx));

      const updatedTasks = tasks.map(t => {
        if (!t.parent_task_id && positionMap.has(t.id)) {
          return { ...t, kanban_position: positionMap.get(t.id)! };
        }
        return t;
      });

      const originalTasks = [...tasks];
      set({ tasks: updatedTasks });

      try {
        for (let idx = 0; idx < reordered.length; idx++) {
          await taskRepo.updateTaskPosition(reordered[idx].id, idx);
        }
      } catch (err) {
        console.error('Reorder task failed, rolling back:', err);
        set({ tasks: originalTasks });
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Tasks - Realtime
    // Note: realtime payloads from Postgres come with snake_case columns.
    // We must normalize them before merging into the store to avoid
    // overwriting camelCase-mapped properties with raw DB columns,
    // which caused rapid re-render flickering ("vibrating" drawer).
    // ─────────────────────────────────────────────────────────────────────────
    realtimeInsertTask: (rawTask) => {
      const { projectId, tasks } = get();
      const normalized = normalizeRealtimeTask(rawTask);
      if (normalized.project_id !== projectId) return;
      if (!normalized.id || tasks.some(t => t.id === normalized.id)) return;
      // For inserts we need a full Task object - merge with raw to ensure all fields exist
      const task = { ...rawTask, ...normalized } as Task;
      set({ tasks: [...tasks, task] });
    },

    realtimeUpdateTask: (taskId, rawUpdates) => {
      const { tasks, tasksSyncing } = get();
      // While a move is in progress, ignore realtime updates to avoid
      // overwriting optimistic state and causing visual duplicates
      if (tasksSyncing) return;
      if (!tasks.some(t => t.id === taskId)) return;
      
      // Only apply safe, known fields from the realtime payload
      // to avoid overwriting the local optimistic state with stale/duplicate data
      const safeUpdates = normalizeRealtimeTask(rawUpdates);
      
      set({
        tasks: tasks.map(t => {
          if (t.id !== taskId) return t;
          // Only overwrite if the remote updated_at is newer than local
          const remoteUpdatedAt = safeUpdates.updated_at || rawUpdates.updated_at;
          if (remoteUpdatedAt && t.updated_at && remoteUpdatedAt <= t.updated_at) {
            return t; // local is same or newer, skip
          }
          return { ...t, ...safeUpdates };
        })
      });
    },

    realtimeDeleteTask: (taskId) => {
      set(state => ({
        tasks: state.tasks.filter(t => t.id !== taskId)
      }));
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Milestones - Load
    // ─────────────────────────────────────────────────────────────────────────
    loadMilestones: async () => {
      const { projectId } = get();
      if (!projectId) return;

      set({ milestonesLoading: true });
      try {
        const milestones = await fetchProjectMilestones(projectId);
        set({ milestones, milestonesLoading: false, milestonesError: null });
      } catch (err: any) {
        set({ milestonesError: err.message, milestonesLoading: false });
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Milestones - CRUD
    // ─────────────────────────────────────────────────────────────────────────
    addMilestone: async (input) => {
      try {
        const newMilestone = await createMilestone(input);
        set(state => ({ milestones: [...state.milestones, newMilestone] }));
        return newMilestone;
      } catch (err) {
        console.error('[projectSyncStore] addMilestone error:', err);
        set({ milestonesError: 'Erreur lors de la création du jalon' });
        return null;
      }
    },

    editMilestone: async (id, patch) => {
      const { milestones } = get();
      const original = milestones.find(m => m.id === id);
      if (!original) return false;

      // Optimistic
      set({
        milestones: milestones.map(m => m.id === id ? { ...m, ...patch } : m)
      });

      try {
        const updated = await updateMilestone(id, patch);
        set({
          milestones: get().milestones.map(m => m.id === id ? { ...m, ...updated } : m)
        });
        return true;
      } catch (err) {
        console.error('[projectSyncStore] editMilestone error:', err);
        set({
          milestones: get().milestones.map(m => m.id === id ? original : m),
          milestonesError: 'Erreur lors de la mise à jour du jalon'
        });
        return false;
      }
    },

    removeMilestone: async (id) => {
      const { milestones } = get();
      const original = milestones.find(m => m.id === id);
      if (!original) return false;

      set({ milestones: milestones.filter(m => m.id !== id) });

      try {
        await deleteMilestone(id);
        return true;
      } catch (err) {
        console.error('[projectSyncStore] removeMilestone error:', err);
        set({
          milestones: [...get().milestones, original],
          milestonesError: 'Erreur lors de la suppression du jalon'
        });
        return false;
      }
    },

    selectMilestone: (id) => {
      set({ selectedMilestoneId: id });
    },

    assignMilestoneToTask: async (taskId, milestoneId) => {
      const { tasks } = get();
      const task = tasks.find(t => t.id === taskId);
      const oldMilestoneId = task?.milestone_id;

      // Optimistic update
      if (task) {
        set({
          tasks: tasks.map(t => t.id === taskId ? { ...t, milestone_id: milestoneId } : t)
        });
      }

      try {
        await assignMilestoneToTask(taskId, milestoneId);
        // Refresh both old and new milestones
        get().refreshAffectedMilestones([oldMilestoneId, milestoneId]);
        return true;
      } catch (err) {
        console.error('[projectSyncStore] assignMilestoneToTask error:', err);
        // Rollback
        if (task) {
          set({
            tasks: get().tasks.map(t => t.id === taskId ? { ...t, milestone_id: oldMilestoneId } : t)
          });
        }
        return false;
      }
    },

    reorderMilestones: async (reorderedMilestones) => {
      const { milestones } = get();
      const original = [...milestones];

      set({ milestones: reorderedMilestones });

      try {
        const positionUpdates = reorderedMilestones.map((m, index) => ({
          id: m.id,
          position: index,
        }));
        await updateMilestonePositions(positionUpdates);
        return true;
      } catch (err) {
        console.error('[projectSyncStore] reorderMilestones error:', err);
        set({ milestones: original, milestonesError: 'Erreur lors du réordonnancement' });
        return false;
      }
    },

    reloadSingleMilestone: async (milestoneId) => {
      try {
        const milestone = await fetchMilestoneById(milestoneId);
        if (milestone) {
          set(state => ({
            milestones: state.milestones.map(m => {
              if (m.id !== milestoneId) return m;
              // Preserve current client-side status to avoid flash from trigger-computed status.
              // loadMilestoneProgress will derive and set the correct status.
              return { ...milestone, status: m.status };
            })
          }));
        }
        return milestone;
      } catch (err) {
        console.error('[projectSyncStore] reloadSingleMilestone error:', err);
        return null;
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Milestones - Realtime
    // ─────────────────────────────────────────────────────────────────────────
    realtimeUpdateMilestone: (milestoneId, updates) => {
      const { milestones } = get();
      if (!milestones.some(m => m.id === milestoneId)) return;
      set({
        milestones: milestones.map(m => m.id === milestoneId ? { ...m, ...updates } : m)
      });
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Progress
    // ─────────────────────────────────────────────────────────────────────────
    loadMilestoneProgress: async (milestoneId) => {
      try {
        const { tasks, terminalStatusCodes } = get();
        const progress = await fetchMilestoneProgress(milestoneId, terminalStatusCodes);
        const todoLikeCodes = new Set(['todo', 'backlog', 'open']);
        const milestoneTasks = tasks.filter(t => t.milestone_id === milestoneId && !t.parent_task_id);
        const allDone = milestoneTasks.length > 0 && milestoneTasks.every(t => terminalStatusCodes.includes(t.status_code));
        const hasStarted = milestoneTasks.some(t => !todoLikeCodes.has(t.status_code));
        const derivedStatus = milestoneTasks.length === 0
          ? 'open'
          : allDone
            ? 'done'
            : hasStarted
              ? 'in_progress'
              : 'open';

        set(state => ({
          milestoneProgress: { ...state.milestoneProgress, [milestoneId]: progress },
          milestones: state.milestones.map(m =>
            m.id === milestoneId ? { ...m, status: derivedStatus } : m
          ),
        }));
        return progress;
      } catch (err) {
        console.error('[projectSyncStore] loadMilestoneProgress error:', err);
        return null;
      }
    },

    loadProjectProgress: async () => {
      const { projectId } = get();
      if (!projectId) return null;

      try {
        const { terminalStatusCodes } = get();
        const progress = await fetchProjectProgress(projectId, terminalStatusCodes);
        set({ projectProgress: progress });
        return progress;
      } catch (err) {
        console.error('[projectSyncStore] loadProjectProgress error:', err);
        return null;
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Sync helpers
    // ─────────────────────────────────────────────────────────────────────────
    refreshAffectedMilestones: async (milestoneIds) => {
      const uniqueIds = [...new Set(milestoneIds.filter((id): id is string => !!id))];
      if (uniqueIds.length === 0) return;

      // Mark as pending
      const pending = get().pendingMilestoneUpdates;
      uniqueIds.forEach(id => pending.add(id));
      set({ pendingMilestoneUpdates: new Set(pending) });

      // Reload each milestone and its progress in parallel
      await Promise.all(
        uniqueIds.map(async (milestoneId) => {
          await get().reloadSingleMilestone(milestoneId);
          await get().loadMilestoneProgress(milestoneId);
        })
      );

      // Also refresh project progress
      await get().loadProjectProgress();

      // Clear pending
      const newPending = get().pendingMilestoneUpdates;
      uniqueIds.forEach(id => newPending.delete(id));
      set({ 
        pendingMilestoneUpdates: new Set(newPending),
        lastSyncAt: Date.now()
      });
    },

    markMilestonePending: (milestoneId) => {
      const pending = get().pendingMilestoneUpdates;
      pending.add(milestoneId);
      set({ pendingMilestoneUpdates: new Set(pending) });
    },
  }))
);

// =============================================================================
// Individual selector hooks - STABLE REFERENCES (no objects)
// =============================================================================

// Task selectors
export const useTasks = () => useProjectSyncStore(s => s.tasks);
export const useTasksLoading = () => useProjectSyncStore(s => s.tasksLoading);
export const useTasksSyncing = () => useProjectSyncStore(s => s.tasksSyncing);
export const useTasksError = () => useProjectSyncStore(s => s.tasksError);

// Milestone selectors
export const useMilestones = () => useProjectSyncStore(s => s.milestones);
export const useMilestonesLoading = () => useProjectSyncStore(s => s.milestonesLoading);
export const useMilestonesError = () => useProjectSyncStore(s => s.milestonesError);
export const useSelectedMilestoneId = () => useProjectSyncStore(s => s.selectedMilestoneId);
export const useMilestoneProgressMap = () => useProjectSyncStore(s => s.milestoneProgress);
export const useProjectProgressData = () => useProjectSyncStore(s => s.projectProgress);

// Sync status selectors
export const useLastSyncAt = () => useProjectSyncStore(s => s.lastSyncAt);
export const usePendingMilestoneUpdates = () => useProjectSyncStore(s => s.pendingMilestoneUpdates);

// NOTE: These composite selectors create new objects and should NOT be used directly in components
// They are kept for backward compatibility but will cause re-renders
// Use individual selectors above instead
